import { yupResolver } from '@hookform/resolvers/yup';
import {
  Stack,
  Dialog,
  Button,
  Avatar,
  MenuItem,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';
import { BankOptions } from 'src/hooks/use-bank-option';
import { BankAccountType } from 'src/type/bank';
import axios, { endpoints } from 'src/utils/axios';
import { toastError } from 'src/utils/format-error';
import * as Yup from 'yup';

type Props = {
  open: boolean;
  onClose: () => void;
  bankAccount: BankAccountType | null;
  forceReload: () => void;
  bankOptions: BankOptions[];
};

const defaultValues = {
  bankId: '',
  accountNumber: '',
  accountName: '',
};

const Schema = Yup.object().shape({
  bankId: Yup.string().required('จำเป็นต้องระบุธนาคาร'),
  accountNumber: Yup.string().required('จำเป็นต้องระบุเลขบัญชี'),
  accountName: Yup.string().required('จำเป็นต้องระบุชื่อบัญชี'),
});

export default function SettingBankFormDialog({
  open,
  onClose,
  bankAccount,
  forceReload,
  bankOptions,
}: Props) {
  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    if (open) {
      if (bankAccount) {
        reset({
          bankId: bankAccount.bankId,
          accountNumber: bankAccount.accountNumber,
          accountName: bankAccount.accountName,
        });
      } else {
        reset(defaultValues);
      }
    }
  }, [bankAccount, open]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      let data: BankAccountType;
      if (bankAccount) {
        const response = await axios.patch<BankAccountType>(
          endpoints.backAccount.update(bankAccount.id),
          values
        );
        ({ data } = response);
      } else {
        const response = await axios.post<BankAccountType>(endpoints.backAccount.create, values);
        ({ data } = response);
      }

      if (!data) {
        throw new Error('ไม่สามารถบันทึกข้อมูลได้');
      }

      enqueueSnackbar('อัพเดทข้อมูลสำเร็จ', { variant: 'success' });
      forceReload();
      beforeClose();
    } catch (error) {
      toastError(error);
    }
  });

  const beforeClose = useCallback(() => {
    onClose();
    reset(defaultValues);
  }, []);

  const actionText = bankAccount ? 'แก้ไข' : 'เพิ่ม';

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={beforeClose}>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle sx={{ pb: 2 }}>{actionText}บัญชีธนาคาร</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 3 }}>
            <RHFSelect name="bankId" label="ธนาคาร" placeholder="ธนาคาร">
              {bankOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Avatar
                      src={option.imageUrl}
                      alt={option.name}
                      sx={{ width: 24, height: 24 }}
                    />

                    {option.name}
                  </Stack>
                </MenuItem>
              ))}
            </RHFSelect>
            <RHFTextField
              name="accountNumber"
              label="เลขบัญชีธนาคาร"
              placeholder="เลขบัญชีธนาคาร"
            />
            <RHFTextField name="accountName" label="ชื่อบัญชีธนาคาร" placeholder="เลขบัญชีธนาคาร" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={beforeClose}>
            ยกเลิก
          </Button>
          <Button variant="contained" disabled={isSubmitting} type="submit">
            บันทึก
          </Button>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
